/**
 * FavoriteButton — the heart: one tap puts a track into «Моё» — since task 77, a clip into
 * «Избранные» of «Видео» — and another takes it out.
 *
 * Task 11, checklist item «Кнопка „в избранное“ в списке, в мини-плеере и на экране „Сейчас
 * играет“». One component in all three places, reading one set (`store/favorites.ts`), so the
 * three can never disagree. It answers the finger at once; the server follows.
 *
 * A toggle, and says so: `aria-pressed` carries «on», and the name stays the same whichever
 * way it is — «В избранное „…“, нажата» is how a screen reader reads a heart that is on. The
 * title is in the name because a list has a heart on every row, and forty buttons called
 * «В избранное» cannot be told apart by ear.
 *
 * It re-renders when its own track's answer changes and at no other time: the selector returns
 * a boolean, and a heart tapped on another row does not change it.
 *
 * Task 77: a clip takes the same heart — its id is in the same set — on its row and as the key
 * «В избранном» under the frame of «Видео», which shows its words under the heart (`words`). A
 * caller that is told of the tap (`onToggle`) keeps that function the same between its renders,
 * or the heart would redraw with it.
 */

import type { JSX } from 'react';
import { memo } from 'react';

import { useFavorites } from '../store/favorites';

/** The accessible name of the heart of `title`. */
export function favoriteLabel(title: string): string {
  return `В избранное «${title}»`;
}

export interface FavoriteButtonProps {
  readonly trackId: number;
  readonly title: string;
  /** The place's own button class: its size and its colours, on and off. */
  readonly className: string;
  readonly iconClassName: string;
  /**
   * Task 77: words under the heart, for a key that shows them. Only drawn: the name stays the
   * heart's own ({@link favoriteLabel}), whichever way it is.
   */
  readonly words?: { readonly off: string; readonly on: string } | undefined;
  /** Task 77: told after a tap, with the heart's new state. */
  readonly onToggle?: ((on: boolean) => void) | undefined;
}

export const FavoriteButton = memo(function FavoriteButton({
  trackId,
  title,
  className,
  iconClassName,
  words,
  onToggle,
}: FavoriteButtonProps): JSX.Element {
  const on = useFavorites((s) => s.ids.has(trackId));
  return (
    <button
      type="button"
      className={className}
      aria-pressed={on}
      aria-label={favoriteLabel(title)}
      onClick={() => {
        useFavorites.getState().toggle(trackId);
        onToggle?.(useFavorites.getState().ids.has(trackId));
      }}
    >
      <svg viewBox="0 0 24 24" aria-hidden="true" className={iconClassName}>
        <path
          d="M12 20.3l-1.3-1.2C6 14.9 3 12.2 3 8.8 3 6.1 5.1 4 7.8 4c1.5 0 3 .7 4.2 1.9C13.2 4.7 14.7 4 16.2 4 18.9 4 21 6.1 21 8.8c0 3.4-3 6.1-7.7 10.3L12 20.3z"
          fill={on ? 'currentColor' : 'none'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      </svg>
      {words !== undefined && <span>{on ? words.on : words.off}</span>}
    </button>
  );
});
